import ScreenLoading from "./app/ScreenLoading";
import { readReportingOwner } from "./lib/reporting";
import { transactionAllocations } from "./lib/allocations";
import { Suspense, useEffect, useMemo, useState } from "react";
import { withPendingTransactions } from "./features/transactions/pending-transactions";
import { LockKeyhole } from "lucide-react";
import { useEntryModal } from "./features/entry/use-entry-modal";
import Empty from "./components/Empty";
import ReconciliationCard from "./features/accounts/ReconciliationCard";
import type {
  EntryPreset,
  Owner,
  OwnerFilter,
  Transaction,
} from "./types";
import {
  dateLabel,
  plural,
  todayIn,
} from "./lib/finance";
import { api } from "./lib/api";
import { ledgerNeed, satisfies, widerNeed, widestNeed } from "./lib/coverage";
import { clearOfflineDashboard, clearOfflineProfile, saveOfflineProfile } from "./lib/offline";
import { routeView, type TransactionFilters, type View } from "./app/views";
import Brand from "./components/Brand";
import { applyDocumentBranding, brandingEnvironment } from "./lib/branding";
import Login from "./features/auth/Login";
import Overview from "./features/overview/Overview";
import Insights from "./features/insights/Insights";
import MoreMenu from "./app/MoreMenu";
import SettingsHome from "./features/settings/SettingsHome";
import AccountsView from "./features/accounts/AccountsView";
import TransactionsView from "./features/transactions/TransactionsView";
import { BottomNav, Sidebar } from "./app/Navigation";
import Topbar from "./app/Topbar";
import PageHeading from "./app/PageHeading";
import StatusBanners from "./app/StatusBanners";
import { useOverviewPreferences } from "./app/use-overview-preferences";
import { useLedgerView } from "./app/use-ledger-view";
import { useSession } from "./app/use-session";
import { useDashboardData } from "./app/use-dashboard-data";
import { useNavigation } from "./app/use-navigation";
import { useOnlineStatus } from "./app/use-online-status";
import { usePendingSync } from "./app/use-pending-sync";
import { useTransactionFilters } from "./app/use-transaction-filters";
import { useReconciliationPrompt } from "./app/use-reconciliation-prompt";
import { usePriceRows } from "./features/prices/use-price-rows";
import { useTransactionDetail } from "./features/transactions/use-transaction-detail";
import { useBulkCategorize } from "./features/transactions/use-bulk-categorize";
import SecondaryScreens from "./app/SecondaryScreens";
import { accountDetailNeed, extraLedgerNeed } from "./app/extra-ledger-need";
import { ProfilesProvider } from "./components/ProfilesContext";
import AppOverlays from "./app/AppOverlays";
import { useAppOverlays } from "./app/use-app-overlays";

export default function App() {
  const [error, setError] = useState("");
  const [view, setView] = useState<View>(() => routeView());
  const [owner, setOwner] = useState<OwnerFilter>(() => readReportingOwner(new URLSearchParams(window.location.search), "All"));
  const [chartCategory, setChartCategory] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(40);
  const overlays = useAppOverlays();
  const [reviewUncategorized, setReviewUncategorized] = useState(() => new URLSearchParams(window.location.search).get("categorize") === "1");
  const { profile, setProfile, checking } = useSession({ setOwner, setError });
  const {
    month, setMonth, changeMonth,
    query, setQuery, dateFilter, setDateFilter, accountFilter, setAccountFilter,
    category, setCategory, subcategory, setSubcategory, type, setType,
    tripFilter, setTripFilter, reporting, setReporting,
    filtersOpen, setFiltersOpen,
  } = useTransactionFilters({ setView, setReviewUncategorized, owner, setOwner });
  // The household's timezone is not known until the first load; the window only has to be roughly
  // right, and anything it misses is fetched when a screen asks for it.
  const need = ledgerNeed({ view, month, today: todayIn() });
  const { data, setData, refreshing, refresh, ensure, pin, offlineSince } = useDashboardData({ profile, setProfile, setError, need, scopeKey: view });
  const brandEnvironment = brandingEnvironment(data?.environment);
  useEffect(() => applyDocumentBranding(brandEnvironment), [brandEnvironment]);
  const online = useOnlineStatus();
  const pendingSync = usePendingSync(() => { void refresh(true); }, !!profile && !checking);
  // pendingSync.items is not read inside, but a change to the queue is what makes the merge stale.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const historyData = useMemo(() => data ? withPendingTransactions(data) : null, [data, pendingSync.items]);
  const { nav, setSettingsDirty } = useNavigation({ view, setView, setAccountDetail: overlays.setAccountDetail, setConfirm: overlays.setConfirm });
  const entry = useEntryModal();
  const { categoryChartStyle, setCategoryChartStyle, dailyChartStyle, setDailyChartStyle, overviewCollapsed, toggleOverviewModule } = useOverviewPreferences();
  const loadingOverview = { collapsed: overviewCollapsed };
  const { priceRows, setPriceRows, trackedCounts } = usePriceRows(data, setError);
  // What only the loaded data can say a screen needs (see extraLedgerNeed). A link to one entry
  // (from Moo, or a shared address) may name one older than what is loaded; that one is pinned.
  const linkedId = new URLSearchParams(window.location.search).get("transaction");
  const linkedMissing = !!linkedId && !!data && !data.transactions.some((t) => t.id === linkedId);
  const extraNeed = extraLedgerNeed({ view, data, reviewUncategorized, tripFilter, priceRows, month });
  // An open account is an overlay: the page behind it stays up while its entries load.
  const overlayNeed = view === "accounts" ? null : accountDetailNeed(data, overlays.accountDetail);
  useEffect(() => {
    if (linkedMissing && linkedId) void pin([linkedId]);
  // `pin` is recreated each render; it asks once per entry, so only a newly missing link matters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedMissing, linkedId]);
  useEffect(() => {
    const wider = widestNeed([extraNeed, overlayNeed]);
    if (wider) void ensure(wider);
  // `ensure` is recreated each render; only a change in what is needed should ask for more.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, need, extraNeed, overlayNeed]);
  const ready = !!data && satisfies(data, extraNeed ? widerNeed(need, extraNeed) : need);
  const overlaysReady = ready && (!overlayNeed || satisfies(data, overlayNeed));
  const transactionDetail = useTransactionDetail({ data, profile, priceRows, refresh, setConfirm: overlays.setConfirm });
  const { setDetail } = transactionDetail;
  const {
    categorySelection, setCategorySelection,
    bulkCategory, setBulkCategory,
    categorizing, categorizeSelected,
  } = useBulkCategorize({ data, owner, refresh, setError, reviewUncategorized });
  const today = todayIn(data?.timezone);
  const { dueReconciliationMonth, reconciliationOpen, setReconciliationOpen } =
    useReconciliationPrompt({ data, profile, today });
  const {
    selectedAccounts, monthTransactions, uncategorizedCount, expenses, spending, income,
    budgetOwner, overallBudget, categoryBudgetRows, chartTotal, categories, filtered, months, monthName,
  } = useLedgerView(
    data,
    { owner, month, today, chartCategory },
    { query, dateFilter, accountFilter, category, subcategory, type, tripFilter, reviewUncategorized, reporting },
    historyData,
  );
  useEffect(() => {
    setChartCategory(null);
  }, [owner, month]);
  // A profile that was deleted or renamed elsewhere leaves the owner filter pointing at nobody.
  useEffect(() => {
    if (owner !== "All" && data?.profiles && !data.profiles.some((p) => p.name === owner)) setOwner("All");
  }, [data?.profiles, owner]);
  useEffect(() => {
    setVisibleCount(40);
  }, [owner, month, accountFilter, category, subcategory, type, query]);
  function openCategory(c: string) {
    if (
      chartCategory === null &&
      expenses.some((t) => transactionAllocations(t).some(p => p.category === c && p.subcategory))
    ) {
      setChartCategory(c);
      return;
    }
    setReporting(null);
    setCategory(chartCategory ?? c);
    setSubcategory(chartCategory === null ? null : c);
    setAccountFilter("All");
    setType("Expense");
    setQuery("");
    setView("transactions");
    window.scrollTo({ top: 0 });
  }
  async function switchProfile(p: Owner) {
    try {
      await api("profile", { profile: p });
      saveOfflineProfile(p);
      setProfile(p);
      setOwner(p);
      setAccountFilter("All");
      overlays.setProfileMenu(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function logout(everywhere = false) {
    try {
      await api(everywhere ? "logout-all" : "logout", {});
      clearOfflineProfile();
      clearOfflineDashboard();
      setProfile(null);
      setData(null);
      overlays.setProfileMenu(false);
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  /** Opens the transactions list on every uncategorised expense, with nothing else filtering it. */
  function startCategorizeReview() {
    setReporting(null);
    setReviewUncategorized(true);
    setCategory("All");
    setType("All");
    setQuery("");
    setAccountFilter("All");
    setCategorySelection([]);
    nav("transactions");
  }
  /**
   * Opens a transaction Moo points to. One it has only just written is not in the loaded data yet,
   * so its id goes in the URL and the refresh that follows opens it.
   */
  function openMooTransaction(id: string) {
    const transaction = data?.transactions.find((t) => t.id === id);
    if (transaction) {
      setDetail(transaction);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set("transaction", id);
    window.history.pushState({ detailNavigation: true }, "", url);
    void refresh(true);
  }
  if (checking)
    return (
      <div className="boot">
        <Brand environment={brandEnvironment} />
        <span className="loading-dot" />
        <p>Opening your household…</p>
      </div>
    );
  if (!profile)
    return (
      <Login
        onLogin={(p) => {
          setProfile(p);
          setOwner(p);
          setError("");
        }}
      />
    );
  const groups = Array.from(
    new Set(filtered.slice(0, visibleCount).map((t) => t.date.slice(0, 10))),
  );
  const showTransactions = (f: TransactionFilters) => {
    if (f.owner !== undefined) setOwner(f.owner);
    if (f.month !== undefined) setMonth(f.month);
    setReporting(f.reporting ?? null);
    setReviewUncategorized(false);
    setDateFilter(f.date ?? "");
    setCategory(f.category ?? "All");
    setAccountFilter(f.account ?? "All");
    setType(f.type ?? "All");
    setTripFilter(f.trip ?? "All");
    setQuery(f.query ?? "");
    nav("transactions");
  };
  const serverAvailable = online && !offlineSince;
  const moreProps = data && { nav, data, today, month, profile, online: serverAvailable };
  const settingsProps = { nav, online: serverAvailable, staging: data?.environment === "staging" };
  const accountsProps = data && {
    data,
    onRefresh: refresh,
    owner,
    today,
    month,
    onOpenAccount: overlays.setAccountDetail,
    onOpenTrip: (tripId: string) => {
      overlays.setTripFocus(tripId);
      nav("trips");
    },
    onOpenGoal: (goalId: string) => {
      overlays.setGoalFocus(goalId);
      nav("goals");
    },
  };
  const transactionsProps = data && {
    data: historyData || data, today, selectedAccounts,
    list: { filtered, groups, visibleCount, setVisibleCount, trackedCounts },
    filters: {
      open: filtersOpen, setOpen: setFiltersOpen, query, setQuery, category, setCategory,
      subcategory, setSubcategory, type, setType, accountFilter, setAccountFilter,
      dateFilter, setDateFilter, tripFilter, setTripFilter, reporting, setReporting,
    },
    review: {
      active: reviewUncategorized, setActive: setReviewUncategorized,
      selection: categorySelection, setSelection: setCategorySelection,
      bulkCategory, setBulkCategory, busy: categorizing, apply: categorizeSelected,
    },
    actions: { onOpenTransaction: (transaction: Transaction) => transaction.pendingKey ? overlays.setPendingDetail(transaction.pendingKey) : setDetail(transaction), dateLabel },
  };
  // Gathered here rather than spread across the markup: Overview draws on a lot, and one object
  // makes the size of that surface visible instead of hiding it in a long attribute list.
  const overviewProps = data && {
    data, month, monthName, owner, today,
    selectedAccounts,
    ledger: { monthTransactions, expenses, spending, income, trackedCounts },
    chart: {
      categories, chartTotal, chartCategory, setChartCategory,
      categoryChartStyle, setCategoryChartStyle, dailyChartStyle, setDailyChartStyle, openCategory,
    },
    budgets: { budgetOwner, overallBudget, categoryBudgetRows },
    collapse: { overviewCollapsed, toggleOverviewModule },
    actions: {
      nav,
      setDetail,
      showTransactions,
      onPayBill: overlays.setPayingBill,
      onAddTransaction: (prefill?: EntryPreset) => entry.show(prefill),
      onOpenTrip: (tripId: string) => {
        overlays.setTripFocus(tripId);
        nav("trips");
      },
    },
  };
  const isStaging = data?.environment === "staging";
  const appVersion =
    (data?.version || "dev") +
    (data?.buildCommit ? ` · ${data.buildCommit}` : "");
  // A tab restored from the browser's page cache keeps running its original bundle, so the
  // version in the header (which comes from the API) can describe a build this page is not
  // actually running. Compare against the commit baked in at build time and say so.
  const builtCommit = import.meta.env.VITE_BUILD_COMMIT;
  const staleBuild = !!builtCommit && !!data?.buildCommit && builtCommit !== data.buildCommit;
  return (
    <ProfilesProvider profiles={data?.profiles}>
    <div className={`app-shell${isStaging ? " staging-shell" : ""}`}>
      {isStaging && (
        <div className="environment-banner" role="status">
          STAGING · Test data only
        </div>
      )}
      <Sidebar environment={brandEnvironment} view={view} nav={nav} appVersion={appVersion} profile={profile} onOpenProfile={() => overlays.setProfileMenu(true)} />
      <div className="main-shell">
        <Topbar environment={brandEnvironment} view={view} nav={nav} version={data?.version || "dev"} appVersion={appVersion} refreshing={refreshing} onRefresh={() => refresh(true)} profile={profile} onOpenProfile={() => overlays.setProfileMenu(true)} />
        <main
          className={`main-content${view === "prices" ? " prices-view" : ""}`}
        >
          <PageHeading
            view={view} profile={profile} data={data}
            month={month} months={months} setMonth={setMonth} changeMonth={changeMonth}
            owner={owner} onOwnerChange={(o) => { setOwner(o); setAccountFilter("All"); }}
          />
          <StatusBanners data={data} error={error} staleBuild={staleBuild} online={online} offlineSince={offlineSince} pendingSync={pendingSync.items} syncing={pendingSync.syncing} syncError={pendingSync.syncError} refreshing={refreshing} onSync={pendingSync.syncNow} onReviewPending={overlays.setPendingDetail} onRetry={() => refresh(true)} />
          {!ready && !error ? (
            <ScreenLoading view={view} older={!!data} overview={loadingOverview} />
          ) : !ready && data ? (
            <Empty title="This needs more of your history" description="Older entries could not be loaded. Use Retry above to try again." />
          ) : (
            data && (
              <Suspense fallback={<ScreenLoading view={view} overview={loadingOverview} />}>
                {view === "overview" && uncategorizedCount > 0 && <button className="primary-button overview-alert" onClick={startCategorizeReview}>{plural(uncategorizedCount, "expense needs", "expenses need")} categorizing →</button>}
                {view === "overview" && dueReconciliationMonth && <ReconciliationCard data={data} month={dueReconciliationMonth} onOpen={() => setReconciliationOpen(true)} />}
                {view === "overview" && overviewProps && <Overview {...overviewProps} />}
                {view === "insights" && <Insights data={data} month={month} owner={owner} today={today} ensure={ensure} online={serverAvailable} onBack={() => nav("more")} showTransactions={showTransactions} onOpenTransaction={setDetail} onOpenBudgets={() => nav("budgets")} />}
                {view === "more" && moreProps && <MoreMenu {...moreProps} />}
                <SecondaryScreens
                  view={view} data={historyData || data} profile={profile} today={today} nav={nav} refresh={refresh} ensure={ensure} pin={pin}
                  priceRows={priceRows} setPriceRows={setPriceRows} setDetail={setDetail}
                  iouFocus={overlays.iouFocus} setIouFocus={overlays.setIouFocus}
                  billFocus={overlays.billFocus} setBillFocus={overlays.setBillFocus}
                  tripFocus={overlays.tripFocus} setTripFocus={overlays.setTripFocus}
                  goalFocus={overlays.goalFocus} setGoalFocus={overlays.setGoalFocus}
                  setSettingsDirty={setSettingsDirty}
                  onCategoriesSaved={(entryOptions) => setData((current) => (current ? { ...current, entryOptions } : current))}
                  onOpenReconciliation={() => setReconciliationOpen(true)}
                  onStartCategorizeReview={startCategorizeReview}
                  onSwitchProfile={switchProfile}
                  onAddTransaction={(prefill) => entry.show(prefill)}
                  online={serverAvailable}
                />
                {view === "settings" && <SettingsHome {...settingsProps} />}
                {view === "accounts" && accountsProps && <AccountsView {...accountsProps} />}
                {view === "transactions" && transactionsProps && <TransactionsView {...transactionsProps} />}
                <footer className="page-footer">
                  <span>
                    <LockKeyhole size={12} /> Your household. Your business.
                  </span>
                </footer>
              </Suspense>
            )
          )}
        </main>
      </div>
      <BottomNav view={view} nav={nav} />
      <AppOverlays
        overlays={overlays}
        data={data}
        ready={overlaysReady}
        profile={profile}
        owner={owner}
        view={view}
        month={month}
        serverAvailable={serverAvailable}
        nav={nav}
        refresh={refresh}
        showTransactions={showTransactions}
        openMooTransaction={openMooTransaction}
        switchProfile={switchProfile}
        logout={logout}
        entry={entry}
        transactionDetail={transactionDetail}
        dueReconciliationMonth={dueReconciliationMonth}
        reconciliationOpen={reconciliationOpen}
        setReconciliationOpen={setReconciliationOpen}
      />
    </div>
    </ProfilesProvider>
  );
}
